/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Component, DestroyRef, Inject, Input, signal} from '@angular/core';
import {resourceOptionParams} from '../../common/controller/signal-admin-resource-api';
import * as _ from 'lodash';
import {TranslateService} from '@ngx-translate/core';

import template from './targeting-template.component.html';
import {Uuid} from '../../../components/util/uuid';
import {TargetingHelper} from '../targeting-helper';
import {TeamApiService} from '../../admin/team/team-api.service';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {
  targetingTemplateSaveErrors,
  truncateTargetingTemplateDescription
} from './targeting-template-save.util';
import {
  SignalAdminResourceListState,
  SignalAdminResourceListStateFactory
} from '../../common/controller/signal-admin-resource-list-state';
import {TargetingTemplateApiService, TargetingTemplateModel} from '../targeting-template-api.service';

export interface TargetingTemplateHook {
  getTemplateData: () => {
    vm: any;
    model: any;
    constant: any;
    summary?: string;
  };
  copyTemplate: (template: any) => void;
}

@Component({
  standalone: false,
  selector: 'adn-targeting-template',
  template
})
export class TargetingTemplateComponent {
  @Input() hook: TargetingTemplateHook;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() targetType: any;
  @Input() overridableTargeting: any;
  @Input() mandatory: boolean;
  @Input() underModel: any;

  readonly enabled = signal(false);
  readonly saved = signal(false);
  readonly saving = signal(false);
  readonly saveErrors = signal<string[]>([]);
  readonly view = signal<'COPY' | 'SAVE' | null>(null);
  readonly saveCount = signal(0);
  refreshCopy = false;

  copyTemplateList: SignalAdminResourceListState<TargetingTemplateModel> | null = null;
  saveModel: any;

  readonly allTeams = signal<any[] | undefined>(undefined);
  readonly yourTeams = signal<any[]>([]);
  readonly teams = signal<any[] | undefined>(undefined);

  constructor(
    @Inject(TeamApiService) private readonly Team: TeamApiService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly targetingTemplateApi: TargetingTemplateApiService,
    private readonly listFactory: SignalAdminResourceListStateFactory,
    private readonly destroyRef: DestroyRef,
    private readonly translate: TranslateService,
  ) {
  }

  get listReady(): boolean {
    return !!this.copyTemplateList && !this.copyTemplateList.loading();
  }

  get hasItems(): boolean {
    return this.displayItems.length > 0;
  }

  get displayItems(): TargetingTemplateModel[] {
    return this.copyTemplateList?.items() || [];
  }

  get listPageSize(): number {
    return this.copyTemplateList?.pageSize || 25;
  }

  get listTotalCount(): number {
    return this.copyTemplateList?.totalCount() || 0;
  }

  get listCurrentPage(): number {
    return this.copyTemplateList?.currentPage() || 1;
  }

  get overrideChecked(): boolean {
    return !!(this.overridableTargeting && this.targetType && this.overridableTargeting[this.targetType.apiType]);
  }

  set overrideChecked(value: boolean) {
    if (this.overridableTargeting && this.targetType) {
      this.overridableTargeting[this.targetType.apiType] = value;
    }
  }

  toggleCopy(): void {
    this.enabled.set(this.view() === 'SAVE' ? true : !this.enabled());
    this.saved.set(false);
    this.saveErrors.set([]);

    if (!this.enabled() || !this.hook) {
      return;
    }

    this.view.set('COPY');
    const tplData = this.hook.getTemplateData();

    if (!this.copyTemplateList) {
      this.copyTemplateList = this.listFactory.create(
        this.targetingTemplateApi,
        {
          listId: 'targetingTargetingTemplates',
          additionalParams: {filterBy: 'type', filterByLike: tplData.constant.templateType},
          syncPageToUrl: false
        },
        this.destroyRef
      );
      this.copyTemplateList.load();
    } else if (this.refreshCopy) {
      this.reloadList();
      this.refreshCopy = false;
    }
  }

  toggleSaved(): void {
    this.enabled.set(this.view() !== 'SAVE' ? true : !this.enabled());
    this.saved.set(false);
    this.saveErrors.set([]);

    if (!this.enabled() || !this.hook) {
      return;
    }

    this.view.set('SAVE');
    const tplData: any = this.hook.getTemplateData();
    tplData.count = TargetingHelper.getSizeOfTargetingTypes(tplData.model, tplData.constant.targets);
    this.saveCount.set(tplData.count);

    this.saveModel = {data: {}};
    const typeId = tplData.constant.id;
    const labelKey = tplData.count === 1
      ? 'targeting.' + typeId + '.' + typeId
      : 'targeting.' + typeId + '.plural';
    let typeLabel = this.translate.instant(labelKey);
    if (typeof typeLabel !== 'string') {
      typeLabel = this.translate.instant('targeting.' + typeId + '.plural');
    }
    if (typeof typeLabel !== 'string') {
      typeLabel = typeId;
    }
    this.saveModel.name = tplData.count + ' ' + typeLabel;
    this.saveModel.description = truncateTargetingTemplateDescription(tplData.summary || this.saveModel.name);
    this.saveModel.data.model = tplData.model;
    this.saveModel.data.vm = tplData.vm;
    this.saveModel.type = tplData.constant.templateType;
    this.saveModel.teams = this.saveModel.teams || [];

    Promise.resolve(this.teamQuery(this.saveModel)).then(() => {
      this.saveModel.teamType = 'YOUR';
    });
  }

  selectTeams(template?: any): void {
    const target = template || this.saveModel;
    target.teams = _.map(this.allTeams(), 'id');
  }

  deselectTeams(template?: any): void {
    const target = template || this.saveModel;
    target.teams = [];
  }

  editInvoked(template: any): void {
    this.saveErrors.set([]);
    template.editable = true;
    template.teams = _.map(template.teams, (team) => team.id || team);
    this.teamQuery(template);
  }

  editCancelled(template: any): void {
    template.editable = false;
  }

  copyInvoked(template: any): void {
    this.hook.copyTemplate(template);
    _.forEach(this.displayItems, (item) => {
      item.copy = false;
    });
    template.copy = true;
  }

  deleteInvoked(template: any): void {
    firstValueFrom(this.targetingTemplateApi.setObjectState(template.id, 'INACTIVE')).then(() => {
      this.reloadList();
    });
  }

  editTemplate(template: any): void {
    if (this.saving()) {
      return;
    }

    this.saving.set(true);
    this.saveErrors.set([]);
    const templateForSave = _.cloneDeep(template);
    const nt = templateForSave as TargetingTemplateModel;

    if (!nt.teamType || nt.teamType === 'YOUR') {
      nt.teams = this.yourTeams();
    }

    firstValueFrom(this.targetingTemplateApi.save(nt)).then(() => {
      this.saving.set(false);
      this.enabled.set(false);
      this.saved.set(true);
      this.refreshCopy = true;
    }, (response: any) => this.handleSaveError(response));
  }

  saveTemplate(): void {
    if (this.saving()) {
      return;
    }

    this.saving.set(true);
    this.saveErrors.set([]);
    const templateForSave = _.cloneDeep(this.saveModel);
    templateForSave.id = Uuid.generate();
    const nt = templateForSave as TargetingTemplateModel;

    if (this.saveModel.teamType === 'ALL') {
      nt.teams = [];
    } else if (this.saveModel.teamType === 'YOUR') {
      nt.teams = this.yourTeams();
    }

    firstValueFrom(this.targetingTemplateApi.save(nt)).then(() => {
      this.saving.set(false);
      this.enabled.set(false);
      this.saved.set(true);
      this.refreshCopy = true;
    }, (response: any) => this.handleSaveError(response));
  }

  onSaveEnter(event: KeyboardEvent): void {
    event.preventDefault();
    this.saveTemplate();
  }

  changePage(page: number, event: Event): void {
    if (!this.copyTemplateList || page < 1) {
      return;
    }
    this.copyTemplateList.changePage(page, event);
  }

  pageStart(): number {
    return this.listPageSize * (this.listCurrentPage - 1) + 1;
  }

  pageEnd(): number {
    return Math.min(this.listPageSize * this.listCurrentPage, this.listTotalCount);
  }

  private reloadList(): void {
    this.copyTemplateList?.load();
  }

  private handleSaveError(response: any): void {
    this.saving.set(false);
    this.saved.set(false);
    this.saveErrors.set(targetingTemplateSaveErrors(response));
  }

  private teamQuery(template: any): Promise<void> | void {
    const doType = () => {
      template.teamType = this.yourTeams().length === _.get(template, ['teams', 'length'])
        ? 'YOUR'
        : 'CUSTOM';
    };

    if (this.allTeams()) {
      doType();
      return;
    }

    return firstValueFrom(this.Team.listOptions(resourceOptionParams({minimal: true}))).then((page: any) => {
      const allTeams = page.results;
      this.allTeams.set(allTeams);
      const isViewAllObjects = this.LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
      const yourTeams = _.filter(allTeams, (g) => {
        return this.LocalUserPermissions.isPartOf(g.network.tagId || g.network.id, g.id);
      });
      this.yourTeams.set(yourTeams);
      this.teams.set(isViewAllObjects ? allTeams : yourTeams);
      doType();
    });
  }
}
